import { XMarkIcon, ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import Avatar from "../../../shared/Avatar";
import Button from "../../../shared/Button";
import Modal from "../../../shared/Modal";
import Select from "../../../shared/Select";
import { useAuth } from "../../../hooks/useAuth";
import useForm from "../../../hooks/useForm";
import request, { renderQueries } from "../../../utils/request";
import { DateRangePicker, Input, Badge, Spinner } from "../../../shared";
import { formatDate } from "../../../utils";
import { useGetDriversQuery } from "../../../redux/services/drivers/driversApi";
import { useGetEmployeesQuery } from "../../../redux/services/employees/employeesApi";
import { useGetManifestsQuery } from "../../../redux/services/manifests/manifestsApi";
import { useNavigate } from "react-router-dom";
import { useConfig } from "../../../hooks/useConfig";
import { getProductTerminology } from "../../../constants/product-terminology";
import { useAddPayoutMutation } from "../../../redux/services/payouts/payoutsApi";
import { toast } from "react-toastify";
import {
  formatOperationalWindowEndForDisplay,
  formatOperationalWindowStartForDisplay,
} from "../../../utils/date.utils";

const CreateModal = ({
  modalOpen,
  setModalOpen,
  renderId = (id) => id,
  ...props
}) => {
  const { user } = useAuth();
  const { appType } = useConfig();
  const terms = getProductTerminology(appType);
  const navigate = useNavigate();
  const { form, updateForm, clearForm, setFormInit } = useForm();
  const [addPayout, { isLoading: addPayoutLoading }] = useAddPayoutMutation();

  // Creation mode: "dateRange" or "manifest"
  const [creationMode, setCreationMode] = useState("dateRange");
  const [selectedManifest, setSelectedManifest] = useState(null);

  // Use RTK Query hooks based on appType
  const { data: driversData, isLoading: driversLoading } = useGetDriversQuery(
    { parent: user.parentCompany, type: "driver" },
    { skip: appType !== "trucking" || !!props.driver }
  );

  const { data: employeesData } = useGetEmployeesQuery({});

  // Determine the driver/employee ID to filter manifests
  const driverOrEmployeeId = props.driver?._id || props.employee?._id || form.driver?._id || form.employee?._id;

  // Fetch manifests without payoutId (only for trucking)
  // If a driver is pre-selected, filter manifests by that driver
  const { data: manifestsData, isLoading: manifestsLoading } = useGetManifestsQuery(
    { 
      page: 1, 
      limit: 100,
      payoutId: "null", // Filter for manifests without payout
      ...(driverOrEmployeeId && { employeeId: driverOrEmployeeId }), // Filter by driver if provided
    },
    { skip: appType !== "trucking" || creationMode !== "manifest" }
  );

  // Transform data for select options
  const driverOptions =
    driversData?.drivers?.map((driver) => ({
      label: driver.fullName,
      value: driver._id,
      ...driver,
    })) || [];

  const employeeOptions =
    employeesData?.employees?.map((employee) => ({
      label: employee?.fullName || "No Name",
      value: employee._id,
      ...employee,
    })) || [];

  // Filter manifests that don't have a payoutId
  const manifestRows =
    (Array.isArray(manifestsData?.manifests) && manifestsData.manifests) ||
    (Array.isArray(manifestsData?.docs) && manifestsData.docs) ||
    (Array.isArray(manifestsData?.data?.manifests) &&
      manifestsData.data.manifests) ||
    (Array.isArray(manifestsData?.data?.docs) && manifestsData.data.docs) ||
    [];

  const availableManifests = (manifestRows || []).filter(
    (manifest) => !manifest.payoutId
  );

  const handleDateUpdate = () => {
    const query = {
      parent: user.parentCompany,
      [appType === "trucking" ? "driverId" : "employeeId"]:
        appType === "trucking" ? form.driver?._id : form.employee?._id,
      payoutId: null,
      route: true,
      startDate: formatDate(form.startDate, null, true),
      endDate: formatDate(form.endDate, null, true),
    };
    getCount(query);
  };

  const getCount = async (query) => {
    query.countOnly = true;
    const endpoint = appType === "trucking" ? "/legs" : "/shifts";
    const res = await request(`${endpoint}?${renderQueries(query)}`, {
      method: "get",
      data: {},
    });
    updateForm({ id: "count", value: res.data.data });
  };

  useEffect(() => {
    if (form.startDate && form.endDate && (form.driver || form.employee)) {
      handleDateUpdate();
    }
  }, [form.startDate, form.endDate, form.driver, form.employee]);

  useEffect(() => {
    if ((props.driver || props.employee) && modalOpen) {
      setFormInit({
        [appType === "trucking" ? "driver" : "employee"]:
          props.driver || props.employee,
      });
    }
  }, [setFormInit, modalOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!modalOpen) {
      setCreationMode("dateRange");
      setSelectedManifest(null);
    }
  }, [modalOpen]);

  function closeModal() {
    setModalOpen(false);
    handleCancel();
    setCreationMode("dateRange");
    setSelectedManifest(null);
  }

  async function savePayout() {
    const postData = {
      ...form,
      parent: user.parentCompany,
    };

    const response = await addPayout(postData).unwrap();

    if (response.status === "success") {
      closeModal();
      navigate("/payouts/" + response.data._id);
    }
  }

  async function savePayoutFromManifest() {
    if (!selectedManifest) return;

    // Handle legs - they might be ObjectIds or populated objects
    const legs = selectedManifest.legs?.map((leg) => ({
      _id: typeof leg === "string" ? leg : leg._id || leg,
    })) || [];

    // Preserve the custom manifest leg order for the payout
    // If manifestSortOrder exists, use it; otherwise use the current legs order
    const legSortOrder =
      selectedManifest.manifestSortOrder && selectedManifest.manifestSortOrder.length > 0
        ? selectedManifest.manifestSortOrder
        : legs.map((leg) => leg._id);

    const data = {
      payDate: selectedManifest.dateCreated,
      startDate: selectedManifest.startDate,
      endDate: selectedManifest.endDate,
      emptyRate: selectedManifest.driver?.meta?.emptyRate || 0,
      mileageRate: selectedManifest.driver?.meta?.mileageRate || 0,
      hourlyRate: selectedManifest.driver?.meta?.hourlyRate || 0,
      note: selectedManifest.note,
      driver: selectedManifest.driver,
      manifestId: selectedManifest._id,
      legs,
      legSortOrder, // Preserve manifest order in payout
      status: "Review",
    };

    const res = await addPayout(data).unwrap();

    toast(res.message, {
      toastId: "add-payout-from-manifest",
      type: res.status,
    });

    if (res.status === "success") {
      closeModal();
      navigate(`/payouts/${res.data._id}`);
    }
  }

  function handleSubmit() {
    if (creationMode === "manifest") {
      savePayoutFromManifest();
    } else {
      savePayout();
    }
  }

  const footer = (
    <div>
      <Button className="mr-3" onClick={closeModal} type="alternative">
        Cancel
      </Button>
      <Button
        onClick={handleSubmit}
        type="primary"
        loading={addPayoutLoading}
        disabled={
          creationMode === "manifest" 
            ? !selectedManifest 
            : (form.count === 0 && form.startDate && form.endDate)
        }
      >
        {creationMode === "manifest" ? "Create from Manifest" : "Submit"}
      </Button>
    </div>
  );

  function handleSelectPerson(selected) {
    updateForm({
      id: appType === "trucking" ? "driver" : "employee",
      value: selected,
    });
  }

  function handleCancel() {
    clearForm(appType === "trucking" ? "driver" : "employee");
    setSelectedManifest(null);
  }

  // Mode selector tabs (only show for trucking)
  const modeSelector = appType === "trucking" && (
    <div className="flex space-x-2 mb-4 border-b pb-3">
      <button
        onClick={() => {
          setCreationMode("dateRange");
          setSelectedManifest(null);
        }}
        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
          creationMode === "dateRange"
            ? "bg-primary-100 text-primary-700 border-b-2 border-primary-500"
            : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
        }`}
      >
        Date Range
      </button>
      <button
        onClick={() => {
          setCreationMode("manifest");
          handleCancel();
        }}
        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center ${
          creationMode === "manifest"
            ? "bg-primary-100 text-primary-700 border-b-2 border-primary-500"
            : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
        }`}
      >
        <ClipboardDocumentListIcon className="w-4 h-4 mr-1" />
        From Manifest
      </button>
    </div>
  );

  // Manifest selection content
  const manifestSelectionContent = (
    <div className="space-y-3">
      {manifestsLoading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size="md" />
          <span className="ml-2 text-gray-500">Loading manifests...</span>
        </div>
      ) : availableManifests.length === 0 ? (
        <div className="text-center py-8">
          <ClipboardDocumentListIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">No available manifests without payouts</p>
          <p className="text-sm text-gray-400 mt-1">
            All manifests have been converted to payouts
          </p>
        </div>
      ) : !selectedManifest ? (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          <p className="text-sm text-gray-600 mb-3">
            Select a manifest to create a payout from:
          </p>
          {availableManifests.map((manifest) => (
            <div
              key={manifest._id}
              onClick={() => setSelectedManifest(manifest)}
              className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Avatar size="xs" src={manifest.driver?.avatar} />
                  <div>
                    <p className="font-medium text-sm">
                      {manifest.driver?.fullName || "Unknown Driver"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {manifest.startDate && manifest.endDate
                        ? `${formatOperationalWindowStartForDisplay(manifest.startDate, "MMM dd")} - ${formatOperationalWindowEndForDisplay(manifest.endDate, "MMM dd, yyyy")}`
                        : "No date range"}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge color="blue" label={`${manifest.legs?.length || 0} legs`} />
                  <p className="text-xs text-gray-500 mt-1">
                    {manifest.status || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center space-x-3">
              <Avatar size="sm" src={selectedManifest.driver?.avatar} />
              <div>
                <p className="font-medium">
                  {selectedManifest.driver?.fullName || "Unknown Driver"}
                </p>
                <p className="text-sm text-gray-500">
                  {selectedManifest.startDate && selectedManifest.endDate
                    ? `${formatOperationalWindowStartForDisplay(selectedManifest.startDate, "MMM dd")} - ${formatOperationalWindowEndForDisplay(selectedManifest.endDate, "MMM dd, yyyy")}`
                    : "No date range"}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSelectedManifest(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              Are you sure you want to create a payout from this manifest?
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              ** This will disable being able to edit this manifest unless the payout has been deleted. **
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Legs:</span>
              <span className="ml-2 font-medium">{selectedManifest.legs?.length || 0}</span>
            </div>
            <div>
              <span className="text-gray-500">Status:</span>
              <span className="ml-2 font-medium">{selectedManifest.status || "N/A"}</span>
            </div>
            {selectedManifest.driver?.meta?.hourlyRate && (
              <div>
                <span className="text-gray-500">Hourly Rate:</span>
                <span className="ml-2 font-medium">
                  ${selectedManifest.driver.meta.hourlyRate}
                </span>
              </div>
            )}
            {selectedManifest.driver?.meta?.mileageRate && (
              <div>
                <span className="text-gray-500">Mileage Rate:</span>
                <span className="ml-2 font-medium">
                  ${selectedManifest.driver.meta.mileageRate}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Date range selection content (original flow)
  const dateRangeContent = (
    <div className="">
      {!form.driver && !form.employee ? (
        <div className="space-y-3">
          <div className="col-span-2 md:col-span-1 xl:col-span-2 max-w-md">
            <Select
              search
              searchKeys={["fullName"]}
              id={renderId(appType === "trucking" ? "driver" : "employee")}
              value={appType === "trucking" ? form.driver : form.employee}
              onChange={handleSelectPerson}
              label={appType === "trucking" ? "Driver" : "Employee"}
              options={appType === "trucking" ? driverOptions : employeeOptions}
              placeholder={`Select ${
                appType === "trucking" ? "Driver" : "Employee"
              }`}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="mb-5 ">
            <AvatarClient
              selected
              onCancel={handleCancel}
              {...(appType === "trucking" ? form.driver : form.employee)}
            />
          </div>
          <div className="mb-5 items-center gap-4 mx-5 grid grid-cols-1 w-1/2 md:w-full md:grid-cols-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <DateRangePicker
                startDate={form.startDate ? new Date(form.startDate) : null}
                endDate={form.endDate ? new Date(form.endDate) : null}
                onChange={([start, end]) => {
                  updateForm((prevForm) => ({
                    ...prevForm,
                    startDate: start ? start.toISOString() : null,
                    endDate: end ? end.toISOString() : null,
                  }));
                }}
                className="w-full"
              />
            </div>
            <div className="">
              <Input
                disabled
                className=" !w-1/2"
                label={`${terms.jobCountLabel}:`}
                value={form.count || 0}
              />
            </div>
          </div>
          {form.count === 0 && form.startDate && form.endDate && (
            <p className="text-red-500 text-sm text-center">
              {terms.noJobsInDateRange}
            </p>
          )}
        </>
      )}
    </div>
  );

  const createModalContent = (
    <div>
      {modeSelector}
      {creationMode === "manifest" ? manifestSelectionContent : dateRangeContent}
    </div>
  );

  const getTitle = () => {
    if (creationMode === "manifest") {
      return selectedManifest ? "Confirm Payout Creation" : "Select a Manifest";
    }
    return form.driver || form.employee
      ? "Select a date range"
      : `Select ${appType === "trucking" ? "a driver" : "an employee"}`;
  };

  const title = <span className="mx-4">{getTitle()}</span>;

  return (
    <Modal
      visible={modalOpen}
      setVisible={setModalOpen}
      onClose={closeModal}
      title={title}
      footer={footer}
      content={createModalContent}
      footerPosition="right"
      contentWrapperclassName="p-3 lg:p-6 space-y-6"
      size={creationMode === "manifest" ? "lg" : "md"}
    />
  );
};

const AvatarClient = (props) => {
  // Get the correct display name from the data structure
  const displayName =
    props.data?.user?.fullName || props.user?.fullName || props.label || "";
  // Get avatar from user object if it exists
  const avatarSrc =
    props.data?.user?.avatar || props.user?.avatar || props.avatar;

  return (
    <div
      onClick={props.onClick}
      className="flex items-center justify-between hover:bg-gray-100 rounded-lg py-2 px-1 cursor-pointer"
    >
      <div className="flex items-center space-x-2">
        <Avatar size="xs" src={avatarSrc} />
        <span className="font-semi-bold text-sm">{displayName}</span>
      </div>
      <div>
        {props.selected && (
          <XMarkIcon onClick={props.onCancel} className="w-5 h-5" />
        )}
      </div>
    </div>
  );
};

export default CreateModal;
